import React from "react";

import {
  FaBirthdayCake,
  FaCalendarAlt,
  FaCheckCircle,
  FaCopy,
  FaInfoCircle,
  FaMedal,
  FaWeight,
} from "react-icons/fa";

import { toast } from "react-toastify";

import CateButton from "../ui/CateButton";

export default function CategoryResultCard({
  result,
  hasCalculated,
}) {
  const copyResult = async () => {
    if (!result) {
      return;
    }

    const parts = [
      `Saison ${result.season}`,
      `Catégorie d'âge : ${result.ageCategoryLabel}`,
      result.veteranCategory
        ? `Classe vétéran : ${result.veteranCategory.label}`
        : null,
      result.weightResult
        ? `Catégorie de poids : ${result.weightResult.label}`
        : "Catégorie de poids non calculée",
    ].filter(Boolean);

    try {
      await navigator.clipboard.writeText(
        parts.join("\n")
      );

      toast.success(
        "Le résultat a été copié."
      );
    } catch {
      toast.error(
        "Le résultat n’a pas pu être copié."
      );
    }
  };

  if (!hasCalculated || !result) {
    return (
      <section className="flex min-h-[540px] flex-col items-center justify-center rounded-[2rem] border border-dashed border-slate-300 bg-white p-8 text-center shadow-[0_20px_60px_rgba(15,23,42,0.06)] dark:border-white/15 dark:bg-slate-900">
        <span className="flex h-20 w-20 items-center justify-center rounded-[1.75rem] bg-orange-100 text-3xl text-orange-600 dark:bg-orange-500/10 dark:text-orange-300">
          <FaMedal />
        </span>

        <h2 className="mt-6 text-2xl font-black text-slate-950 dark:text-white">
          Votre résultat apparaîtra ici
        </h2>

        <p className="mt-3 max-w-sm text-sm leading-7 text-slate-500 dark:text-slate-400">
          Renseignez votre date de naissance,
          votre genre et éventuellement votre
          poids pour connaître vos catégories.
        </p>
      </section>
    );
  }

  const genderLabel =
    result.gender === "F"
      ? "Féminin"
      : "Masculin";

  return (
    <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.10)] dark:border-white/10 dark:bg-slate-900">
      <header className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-orange-950 p-6 text-white sm:p-7">
        <div className="absolute -right-16 -top-16 h-52 w-52 rounded-full bg-orange-500/20 blur-3xl" />

        <div className="relative flex items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-400/15 px-3 py-1.5 text-xs font-black text-emerald-300">
              <FaCheckCircle />
              Calcul terminé
            </div>

            <h2 className="mt-4 text-2xl font-black sm:text-3xl">
              Votre catégorie
            </h2>

            <p className="mt-2 text-sm text-slate-300">
              Saison {result.season} ·{" "}
              {genderLabel}
            </p>
          </div>

          <button
            type="button"
            onClick={copyResult}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
            aria-label="Copier le résultat"
            title="Copier le résultat"
          >
            <FaCopy />
          </button>
        </div>
      </header>

      <div className="space-y-4 p-5 sm:p-7">
        <ResultBlock
          icon={FaBirthdayCake}
          eyebrow="Catégorie d’âge"
          value={result.ageCategoryLabel}
          description={`Années de naissance : ${result.ageCategoryBirthYears}`}
          accent="orange"
        />

        <ResultBlock
          icon={FaWeight}
          eyebrow="Catégorie de poids"
          value={
            result.weightResult?.label ||
            "Poids non renseigné"
          }
          description={
            result.weightResult?.type ===
            "morphological"
              ? "Les enfants sont répartis en groupes morphologiques."
              : result.weightResult
                ? "La pesée officielle détermine la catégorie définitive."
                : "Renseignez votre poids pour compléter le calcul."
          }
          accent="emerald"
        />

        {result.veteranCategory ? (
          <ResultBlock
            icon={FaMedal}
            eyebrow="Classe vétéran"
            value={
              result.veteranCategory.label
            }
            description={`${result.veteranCategory.minAge} à ${
              result.veteranCategory.maxAge ===
              Infinity
                ? "80 ans et plus"
                : `${result.veteranCategory.maxAge} ans`
            }`}
            accent="blue"
          />
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <MiniInfo
            icon={FaCalendarAlt}
            label="Année de référence"
            value={result.referenceYear}
          />

          <MiniInfo
            icon={FaBirthdayCake}
            label="Âge de référence"
            value={`${result.referenceAge} ans`}
          />
        </div>

        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-100">
          <div className="flex items-start gap-3">
            <FaInfoCircle className="mt-1 shrink-0" />

            <p>
              Ce résultat est indicatif.
              Consultez toujours le règlement et
              la convocation de la compétition.
            </p>
          </div>
        </div>

        <CateButton
          icon={FaCopy}
          variant="secondary"
          className="w-full"
          onClick={copyResult}
        >
          Copier mon résultat
        </CateButton>
      </div>
    </section>
  );
}

function ResultBlock({
  icon: Icon,
  eyebrow,
  value,
  description,
  accent,
}) {
  const accents = {
    orange:
      "bg-orange-100 text-orange-600 dark:bg-orange-500/10 dark:text-orange-300",

    emerald:
      "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300",

    blue:
      "bg-blue-100 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300",
  };

  return (
    <article className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/[0.03]">
      <div className="flex items-start gap-4">
        <span
          className={[
            "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-xl",
            accents[accent],
          ].join(" ")}
        >
          <Icon />
        </span>

        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">
            {eyebrow}
          </p>

          <p className="mt-1 text-2xl font-black text-slate-950 dark:text-white">
            {value}
          </p>

          <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
            {description}
          </p>
        </div>
      </div>
    </article>
  );
}

function MiniInfo({
  icon: Icon,
  label,
  value,
}) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 p-4 dark:border-white/10">
      <Icon className="text-orange-500" />

      <div>
        <p className="text-xs text-slate-400">
          {label}
        </p>

        <p className="font-black text-slate-800 dark:text-white">
          {value}
        </p>
      </div>
    </div>
  );
}